import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import Footer from '@/Company/About/Footer';
import Navbar from '@/Company/About/Navbar';
import { Button } from '@/components/ui/button';
import { Container } from '@/components/ui/container';
import { ArrowLeft, FileText, CheckCircle, Sparkles, Download } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

// Import all preview components
import ProfessionalResumePreview from "./ProfessionalResumePreview";
import ModernResumePreview from "./ModernResumePreview";
import TechResumePreview from "./TechResumePreview";
import MarketingSpecialistResumePreview from "./MarketingSpecialistResumePreview";
import EnglishTeacherResumePreview from "./EnglishTeacherResumePreview";
import ConsultingProfessionalResumePreview from "./ConsultingProfessionalResumePreview";
import SecurityResumePreview from "./SecurityResumePreview";
import SimpleResumePreview from "./SimpleResumePreview";
import BusinessDevResumePreview from "./BusinessDevResumePreview";
import ExecutiveResumePreview from "./ExecutiveResumePreview";
import ScienceTeacherResumePreview from "./ScienceTeacherResumePreview";
import PETeacherResumePreview from "./PETeacherResumePreview";
import TCSResumePreview from "./TCSResumePreview";
import ATSResumePreview from "./ATSResumePreview";

const SAVED_TEMPLATES_STORAGE_KEY = "savedCvTemplates";
const FAVORITES_STORAGE_KEY = "cvTemplatesFavorites";

const loadTemplatesFromStorage = () => {
  try {
    const raw = localStorage.getItem(SAVED_TEMPLATES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map((item) => ({
        ...item,
        data: item.data || {},
      }));
    }
  } catch (error) {
    console.error("Failed to read saved CV templates", error);
  }
  return [];
};

const loadFavorites = () => {
  try {
    const raw = localStorage.getItem(FAVORITES_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(parsed);
  } catch (error) {
    return new Set();
  }
};

const saveFavorites = (favorites) => {
  try {
    localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(Array.from(favorites)));
  } catch (error) {
    console.error("Failed to save favorites", error);
  }
};

const persistTemplates = (templates) => {
  try {
    localStorage.setItem(
      SAVED_TEMPLATES_STORAGE_KEY,
      JSON.stringify(templates)
    );
  } catch (error) {
    console.error("Failed to persist CV templates", error);
  }
};

const formatTimestamp = (isoString) => {
  if (!isoString) return "Unknown date";
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(isoString));
  } catch (error) {
    return new Date(isoString).toLocaleString();
  }
};

const formatRelativeTime = (isoString) => {
  if (!isoString) return "Unknown";
  try {
    const now = new Date();
    const date = new Date(isoString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    return formatTimestamp(isoString);
  } catch (error) {
    return formatTimestamp(isoString);
  }
};

const getTemplatePreviewComponent = (templateId) => {
  const templateMap = {
    professional: ProfessionalResumePreview,
    modern: ModernResumePreview,
    "tech-resume": TechResumePreview,
    "computer-teacher": TechResumePreview,
    "marketing-specialist": MarketingSpecialistResumePreview,
    "english-teacher": EnglishTeacherResumePreview,
    "consulting-profile": ConsultingProfessionalResumePreview,
    "security-manager": SecurityResumePreview,
    simple: SimpleResumePreview,
    "business-development": BusinessDevResumePreview,
    "cv-guy": ExecutiveResumePreview,
    "science-teacher": ScienceTeacherResumePreview,
    "pe-teacher": PETeacherResumePreview,
    tcs: TCSResumePreview,
    ats: ATSResumePreview,
  };
  return templateMap[templateId] || ProfessionalResumePreview;
};

const TemplateThumbnail = ({ templateId, resumeData, isFavorite }) => {
  const templateConfig = {
    professional: { color: '#3b82f6', accent: '#2563eb' },
    modern: { color: '#8b5cf6', accent: '#7c3aed' },
    'tech-resume': { color: '#10b981', accent: '#059669' },
    'marketing-specialist': { color: '#f59e0b', accent: '#d97706' },
    'english-teacher': { color: '#ec4899', accent: '#db2777' },
    'consulting-profile': { color: '#06b6d4', accent: '#0891b2' },
    'security-manager': { color: '#6366f1', accent: '#4f46e5' },
    simple: { color: '#64748b', accent: '#475569' },
    'business-development': { color: '#14b8a6', accent: '#0d9488' },
    'cv-guy': { color: '#1f2937', accent: '#111827' },
    'science-teacher': { color: '#a855f7', accent: '#9333ea' },
    'pe-teacher': { color: '#f97316', accent: '#ea580c' },
    tcs: { color: '#0ea5e9', accent: '#0284c7' },
    ats: { color: '#84cc16', accent: '#65a30d' },
  };

  const config = templateConfig[templateId] || templateConfig.simple;
  const name = resumeData?.full_name || 'Resume Preview';
  const initials = name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div 
      className="cv-template-thumbnail"
      style={{ 
        background: `linear-gradient(135deg, ${config.color}15 0%, ${config.accent}08 100%)`,
        borderTop: `3px solid ${config.color}`
      }}
    >
      {isFavorite && (
        <div className="cv-thumbnail-favorite-badge">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#fbbf24" stroke="#fbbf24">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
          </svg>
        </div>
      )}
      <div className="cv-thumbnail-content">
        <div className="cv-thumbnail-initials-circle" style={{ 
          background: `linear-gradient(135deg, ${config.color} 0%, ${config.accent} 100%)`,
          color: '#ffffff'
        }}>
          {initials}
        </div>
        <div className="cv-thumbnail-name">{name}</div>
      </div>
    </div>
  );
};

const QuickActionsMenu = ({ template, onEdit, onDownload, onRename, onDelete, onToggleFavorite, isFavorite, onClose }) => {
  const menuRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 0);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleAction = (action, actionName = 'unknown') => {
    console.log(`[QuickActionsMenu] handleAction called for: ${actionName}`);
    setIsOpen(false);
    // Use setTimeout to ensure menu closes before action executes
    setTimeout(() => {
      console.log(`[QuickActionsMenu] Executing action: ${actionName}`);
      if (action && typeof action === 'function') {
        action();
      } else {
        console.error(`[QuickActionsMenu] Action is not a function:`, action);
      }
    }, 150);
  };

  return (
    <div
      className="cv-quick-actions-menu"
      ref={menuRef}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        className="cv-quick-actions-trigger"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        title="More options"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="5" r="1"/>
          <circle cx="12" cy="12" r="1"/>
          <circle cx="12" cy="19" r="1"/>
        </svg>
      </button>
      {isOpen && (
        <div className="cv-quick-actions-dropdown">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleAction(onToggleFavorite);
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill={isFavorite ? "#fbbf24" : "none"} stroke="currentColor" strokeWidth="2">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            {isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleAction(onDownload);
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Download PDF
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleAction(onEdit);
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            Edit
          </button>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              console.log('[QuickActionsMenu] Rename button clicked');
              console.log('[QuickActionsMenu] onRename function:', onRename);
              handleAction(() => {
                console.log('[QuickActionsMenu] Inside rename action callback');
                if (onRename) {
                  onRename();
                } else {
                  console.error('[QuickActionsMenu] onRename is not defined');
                }
              }, 'rename');
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 7h16M4 12h16M4 17h16"/>
            </svg>
            Rename
          </button>
          <div className="cv-quick-actions-divider"></div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleAction(onDelete);
            }}
            className="danger"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            </svg>
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

const PreviewModal = ({ template, onClose, onDownload, onEdit, onDelete, zoom, onZoomChange }) => {
  const previewRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const PreviewComponent = getTemplatePreviewComponent(
    template.templateId || template.template_name
  );

  const handlePrint = useReactToPrint({
    content: () => previewRef.current,
    pageStyle: `
      @page {
        size: A4;
        margin: 0;
      }
      @media print {
        html, body {
          margin: 0;
          padding: 0;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `,
    documentTitle: template.name || "resume",
    removeAfterPrint: false,
    copyStyles: true,
    onBeforePrint: () => {
      setIsDownloading(true);
    },
    onAfterPrint: () => {
      setIsDownloading(false);
    },
  });

  const handleDownload = () => {
    if (previewRef.current) {
      handlePrint();
    }
  };

  const zoomLevels = [50, 75, 100, 125, 150, 200];
  const currentZoomIndex = zoomLevels.indexOf(zoom);

  const handleZoomIn = () => {
    if (currentZoomIndex < zoomLevels.length - 1) {
      onZoomChange(zoomLevels[currentZoomIndex + 1]);
    }
  };

  const handleZoomOut = () => {
    if (currentZoomIndex > 0) {
      onZoomChange(zoomLevels[currentZoomIndex - 1]);
    }
  };

  const handleZoomFit = () => {
    onZoomChange(100);
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        if (e.deltaY < 0) {
          handleZoomIn();
        } else {
          handleZoomOut();
        }
      }
    };
    document.addEventListener("keydown", handleEscape);
    document.addEventListener("wheel", handleWheel, { passive: false });
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("wheel", handleWheel);
      document.body.style.overflow = "unset";
    };
  }, [onClose, zoom]);

  return (
    <div className="cv-preview-modal-overlay" onClick={onClose}>
      <div
        className="cv-preview-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cv-preview-modal-header">
          <div className="cv-preview-modal-title">
            <h2>{template.name || "Untitled Resume"}</h2>
            <span className="cv-preview-template-badge">
              {template.templateLabel || template.templateId}
            </span>
          </div>
          <div className="cv-preview-modal-controls">
            <div className="cv-preview-zoom-controls">
              <button
                onClick={handleZoomOut}
                disabled={currentZoomIndex === 0}
                className="cv-zoom-btn"
                title="Zoom Out (Ctrl + Scroll)"
              >
                ➖
              </button>
              <select
                value={zoom}
                onChange={(e) => onZoomChange(Number(e.target.value))}
                className="cv-zoom-select"
              >
                {zoomLevels.map(level => (
                  <option key={level} value={level}>{level}%</option>
                ))}
              </select>
              <button
                onClick={handleZoomIn}
                disabled={currentZoomIndex === zoomLevels.length - 1}
                className="cv-zoom-btn"
                title="Zoom In (Ctrl + Scroll)"
              >
                ➕
              </button>
              <button
                onClick={handleZoomFit}
                className="cv-zoom-btn"
                title="Fit to Width"
              >
                🔍
              </button>
            </div>
            <div className="cv-preview-modal-actions">
              <button
                onClick={handleDownload}
                disabled={isDownloading}
                className="cv-preview-btn cv-preview-btn-primary"
              >
                {isDownloading ? "Downloading..." : "📥 Download PDF"}
              </button>
              <button
                onClick={onEdit}
                className="cv-preview-btn cv-preview-btn-secondary"
              >
                ✏️ Edit
              </button>
              <button
                onClick={onDelete}
                className="cv-preview-btn cv-preview-btn-danger"
              >
                🗑️ Delete
              </button>
              <button
                onClick={onClose}
                className="cv-preview-btn cv-preview-btn-close"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
        <div className="cv-preview-modal-body">
          <div className="cv-preview-scroll-container">
            <div 
              ref={previewRef} 
              className="cv-preview-wrapper"
              style={{ 
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'top center',
                transition: 'transform 0.2s ease',
                marginBottom: `${(zoom / 100 - 1) * 297}mm`
              }}
            >
              <PreviewComponent
                componentRef={previewRef}
                resumeData={template.data || {}}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CvTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("date-desc");
  const [filterType, setFilterType] = useState("all");
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusVariant, setStatusVariant] = useState("info");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(100);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const toastTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const statusTimeoutRef = useRef(null);
  
  // Advanced filtering states
  const [selectedTemplateTypes, setSelectedTemplateTypes] = useState([]);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [selectedTags, setSelectedTags] = useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const userEmail = localStorage.getItem("user_email");
  const companyEmail = localStorage.getItem("company_email");
  const isAuthenticated = userEmail || companyEmail;

  const refreshTemplates = () => {
    const stored = loadTemplatesFromStorage();
    const favs = loadFavorites();
    setFavorites(favs);
    setTemplates(stored);
  };

  useEffect(() => {
    refreshTemplates();
    return () => {
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
    };
  }, []);

  // Debug logging for rename state
  useEffect(() => {
    console.log('[CvTemplatesPage] renamingId state changed to:', renamingId);
    console.log('[CvTemplatesPage] renameValue state changed to:', renameValue);
  }, [renamingId, renameValue]);

  useEffect(() => {
    let filtered = [...templates];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item => 
        (item.name || "").toLowerCase().includes(query) ||
        (item.templateLabel || item.templateId || "").toLowerCase().includes(query) ||
        (item.data?.full_name || "").toLowerCase().includes(query) ||
        (item.tags || []).some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Advanced: Multi-select template type filter
    if (selectedTemplateTypes.length > 0) {
      filtered = filtered.filter(item => {
        const type = item.templateId || item.template_name;
        return selectedTemplateTypes.includes(type);
      });
    } else if (filterType !== "all") {
      // Fallback to single select
      filtered = filtered.filter(item => 
        (item.templateId || item.template_name) === filterType
      );
    }

    // Advanced: Date range filter
    if (dateRange.start || dateRange.end) {
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.savedAt || 0);
        if (dateRange.start && itemDate < new Date(dateRange.start)) return false;
        if (dateRange.end) {
          const endDate = new Date(dateRange.end);
          endDate.setHours(23, 59, 59, 999);
          if (itemDate > endDate) return false;
        }
        return true;
      });
    }

    // Advanced: Tags filter
    if (selectedTags.length > 0) {
      filtered = filtered.filter(item => {
        const itemTags = item.tags || [];
        return selectedTags.every(selectedTag => itemTags.includes(selectedTag));
      });
    }

    // Favorites filter
    if (showFavoritesOnly) {
      filtered = filtered.filter(item => favorites.has(item.id));
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === "date-desc") {
        const aDate = new Date(a.savedAt || 0).getTime();
        const bDate = new Date(b.savedAt || 0).getTime();
        return bDate - aDate;
      } else if (sortBy === "date-asc") {
        const aDate = new Date(a.savedAt || 0).getTime();
        const bDate = new Date(b.savedAt || 0).getTime();
        return aDate - bDate;
      } else if (sortBy === "name-asc") {
        return (a.name || "").localeCompare(b.name || "");
      } else if (sortBy === "name-desc") {
        return (b.name || "").localeCompare(a.name || "");
      } else if (sortBy === "favorites-first") {
        const aFav = favorites.has(a.id);
        const bFav = favorites.has(b.id);
        if (aFav && !bFav) return -1;
        if (!aFav && bFav) return 1;
        return new Date(b.savedAt || 0).getTime() - new Date(a.savedAt || 0).getTime();
      }
      return 0;
    });

    setFilteredTemplates(filtered);
  }, [templates, searchQuery, sortBy, filterType, showFavoritesOnly, favorites, selectedTemplateTypes, dateRange, selectedTags]);

  const showStatus = (message, variant = "info", timeout = 4000) => {
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current);
    }
    setStatusMessage(message);
    setStatusVariant(variant);
    if (timeout > 0) {
      statusTimeoutRef.current = setTimeout(() => {
        setStatusMessage(null);
      }, timeout);
    }
  };

  const showToastNotification = (message, timeout = 3000) => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToastMessage(message);
    setShowToast(true);
    if (timeout > 0) {
      toastTimeoutRef.current = setTimeout(() => {
        setShowToast(false);
        setTimeout(() => {
          setToastMessage("");
        }, 300);
      }, timeout);
    }
  };

  useEffect(() => {
    // Smooth page entrance animation
    document.body.style.opacity = '0';
    document.body.style.transition = 'opacity 0.3s ease-in';
    setTimeout(() => {
      document.body.style.opacity = '1';
    }, 50);
  }, []);


  const handleCardClick = (template) => {
    setSelectedTemplate(template);
    setShowPreview(true);
    setPreviewZoom(100);
  };

  const handleOpen = (id) => {
    navigate(`/create-resume?view=editor&savedTemplateId=${encodeURIComponent(id)}`);
  };

  const handleDownload = (id) => {
    navigate(
      `/create-resume?view=editor&savedTemplateId=${encodeURIComponent(
        id
      )}&autoDownload=true`
    );
  };

  const handleDelete = (id) => {
    const updated = templates.filter((item) => item.id !== id);
    setTemplates(updated);
    persistTemplates(updated);
    const newFavorites = new Set(favorites);
    newFavorites.delete(id);
    setFavorites(newFavorites);
    saveFavorites(newFavorites);
    showToastNotification("Template removed from CV Templates.");
    if (selectedTemplate && selectedTemplate.id === id) {
      setShowPreview(false);
      setSelectedTemplate(null);
    }
  };

  const handleRename = (template) => {
    console.log('[CvTemplatesPage] handleRename called');
    console.log('[CvTemplatesPage] Template:', template);
    console.log('[CvTemplatesPage] Template ID:', template?.id);
    console.log('[CvTemplatesPage] Template Name:', template?.name);
    
    if (!template || !template.id) {
      console.error('[CvTemplatesPage] Invalid template provided to handleRename');
      return;
    }
    
    const newName = template.name || "Untitled Resume";
    console.log('[CvTemplatesPage] Setting renamingId to:', template.id);
    console.log('[CvTemplatesPage] Setting renameValue to:', newName);
    
    setRenamingId(template.id);
    setRenameValue(newName);
    
    // Verify state was set
    setTimeout(() => {
      console.log('[CvTemplatesPage] State after setRenamingId - renamingId should be:', template.id);
    }, 0);
  };

  const handleRenameSubmit = (id) => {
    if (!renameValue.trim()) {
      showStatus("Name cannot be empty.", "warning");
      setRenamingId(null);
      setRenameValue("");
      return;
    }
    const template = templates.find(item => item.id === id);
    if (template && template.name === renameValue.trim()) {
      // Name hasn't changed, just cancel rename
      setRenamingId(null);
      setRenameValue("");
      return;
    }
    const updated = templates.map(item =>
      item.id === id ? { ...item, name: renameValue.trim() } : item
    );
    setTemplates(updated);
    persistTemplates(updated);
    setRenamingId(null);
    setRenameValue("");
    showToastNotification("Template renamed successfully.");
  };

  const handleRenameCancel = () => {
    setRenamingId(null);
    setRenameValue("");
  };

  const handleToggleFavorite = (template) => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(template.id)) {
      newFavorites.delete(template.id);
      showToastNotification("Removed from favorites.");
    } else {
      newFavorites.add(template.id);
      showToastNotification("Added to favorites.");
    }
    setFavorites(newFavorites);
    saveFavorites(newFavorites);
  };

  const handleClearAll = () => {
    if (window.confirm("Are you sure you want to delete all templates? This action cannot be undone.")) {
      setTemplates([]);
      persistTemplates([]);
      setFavorites(new Set());
      saveFavorites(new Set());
      showStatus("All saved templates cleared.", "warning");
      setShowPreview(false);
      setSelectedTemplate(null);
    }
  };

  const handleBack = () => {
    navigate("/create-resume?view=templates");
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  };

  const getUniqueTemplateTypes = () => {
    const types = new Set();
    templates.forEach(item => {
      const type = item.templateId || item.template_name;
      if (type) types.add(type);
    });
    return Array.from(types).sort();
  };

  // Analytics calculations
  const calculateAnalytics = () => {
    const now = new Date();
    const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    const totalCVs = templates.length;
    const recent7Days = templates.filter(t => new Date(t.savedAt || 0) >= last7Days).length;
    const recent30Days = templates.filter(t => new Date(t.savedAt || 0) >= last30Days).length;
    
    // Template usage statistics
    const templateUsage = {};
    templates.forEach(t => {
      const type = t.templateId || t.template_name || 'Unknown';
      templateUsage[type] = (templateUsage[type] || 0) + 1;
    });
    
    const sortedUsage = Object.entries(templateUsage)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
    
    const mostUsedTemplate = sortedUsage[0] ? sortedUsage[0][0] : 'N/A';
    const mostUsedCount = sortedUsage[0] ? sortedUsage[0][1] : 0;
    
    return {
      totalCVs,
      recent7Days,
      recent30Days,
      templateUsage: sortedUsage,
      mostUsedTemplate,
      mostUsedCount,
      favoritesCount: favorites.size
    };
  };

  const analytics = calculateAnalytics();

  // Get all unique tags from templates
  const getAllTags = () => {
    const tagSet = new Set();
    templates.forEach(t => {
      if (t.tags && Array.isArray(t.tags)) {
        t.tags.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  };

  const allTags = getAllTags();

  // Remove tag from template
  const handleRemoveTag = (templateId, tag) => {
    const updated = templates.map(t => {
      if (t.id === templateId) {
        const tags = (t.tags || []).filter(t => t !== tag);
        return { ...t, tags };
      }
      return t;
    });
    setTemplates(updated);
    persistTemplates(updated);
    showToastNotification("Tag removed successfully.");
  };


  return (
    <div className="min-h-screen bg-background text-foreground w-full">
      <style>{`
        .cv-templates-container {
          width: 100%;
          max-width: 1600px;
          margin: 0 auto;
          padding: 48px 32px;
          box-sizing: border-box;
        }
        .cv-templates-header {
          margin-bottom: 40px;
        }
        .cv-templates-header h1 {
          margin: 0 0 8px 0;
          font-size: 36px;
          font-weight: 400;
          color: #202124;
          letter-spacing: -0.5px;
          font-family: 'Google Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        .cv-templates-header p {
          margin: 0;
          color: #5f6368;
          font-size: 14px;
          line-height: 1.5;
          font-weight: 400;
        }
        /* Analytics Dashboard */
        .cv-analytics-dashboard {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 16px;
          margin-bottom: 32px;
        }
        .cv-analytics-card {
          background: #ffffff;
          border: 1px solid #dadce0;
          border-radius: 8px;
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 16px;
          box-shadow: 0 1px 2px rgba(60, 64, 67, 0.3), 0 1px 3px 1px rgba(60, 64, 67, 0.15);
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .cv-analytics-card:hover {
          box-shadow: 0 2px 6px rgba(60, 64, 67, 0.3), 0 4px 8px 2px rgba(60, 64, 67, 0.15);
          transform: translateY(-2px);
        }
        .cv-analytics-icon {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .cv-analytics-content {
          flex: 1;
        }
        .cv-analytics-value {
          font-size: 24px;
          font-weight: 500;
          color: #202124;
          margin-bottom: 4px;
          line-height: 1.2;
        }
        .cv-analytics-label {
          font-size: 13px;
          color: #5f6368;
          font-weight: 400;
        }
        /* Advanced Filters */
        .cv-advanced-filters-toggle {
          margin-bottom: 16px;
        }
        .cv-advanced-toggle-btn {
          background: #ffffff;
          border: 1px solid #dadce0;
          border-radius: 4px;
          padding: 10px 16px;
          font-size: 14px;
          font-weight: 500;
          color: #202124;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .cv-advanced-toggle-btn:hover {
          background: #f8f9fa;
          border-color: #4285f4;
        }
        .cv-advanced-toggle-btn.active {
          background: #e8f0fe;
          border-color: #4285f4;
          color: #1a73e8;
        }
        .cv-advanced-filters-panel {
          background: #ffffff;
          border: 1px solid #dadce0;
          border-radius: 8px;
          padding: 24px;
          margin-bottom: 24px;
          box-shadow: 0 1px 2px rgba(60, 64, 67, 0.1);
        }
        .cv-advanced-filter-group {
          margin-bottom: 24px;
        }
        .cv-advanced-filter-group:last-child {
          margin-bottom: 0;
        }
        .cv-filter-label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: #202124;
          margin-bottom: 12px;
        }
        .cv-multi-select-container {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          align-items: center;
        }
        .cv-multi-select-option {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          padding: 8px 12px;
          border: 1px solid #dadce0;
          border-radius: 4px;
          background: #ffffff;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          font-size: 14px;
          color: #202124;
        }
        .cv-multi-select-option:hover {
          background: #f8f9fa;
          border-color: #4285f4;
        }
        .cv-multi-select-option input[type="checkbox"] {
          width: 16px;
          height: 16px;
          cursor: pointer;
          accent-color: #1a73e8;
        }
        .cv-date-range-container {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }
        .cv-date-input {
          padding: 10px 12px;
          border: 1px solid #dadce0;
          border-radius: 4px;
          font-size: 14px;
          background: #ffffff;
          color: #202124;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .cv-date-input:hover {
          border-color: #4285f4;
        }
        .cv-date-input:focus {
          outline: none;
          border-color: #4285f4;
          box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.2);
        }
        .cv-date-separator {
          color: #5f6368;
          font-size: 14px;
        }
        .cv-tags-filter-container {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .cv-tags-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .cv-tag-filter-option {
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
        }
        .cv-tag-filter-option input[type="checkbox"] {
          width: 16px;
          height: 16px;
          cursor: pointer;
          accent-color: #1a73e8;
        }
        .cv-tag-badge {
          padding: 6px 12px;
          background: #f1f3f4;
          color: #202124;
          border-radius: 16px;
          font-size: 13px;
          font-weight: 500;
          border: 1px solid #dadce0;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .cv-tag-filter-option:hover .cv-tag-badge {
          background: #e8f0fe;
          border-color: #4285f4;
          color: #1a73e8;
        }
        .cv-tag-filter-option input[type="checkbox"]:checked + .cv-tag-badge {
          background: #e8f0fe;
          border-color: #1a73e8;
          color: #1a73e8;
        }
        .cv-clear-filter-btn {
          padding: 6px 12px;
          background: #f1f3f4;
          border: 1px solid #dadce0;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 500;
          color: #5f6368;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .cv-clear-filter-btn:hover {
          background: #e8eaed;
          border-color: #dadce0;
          color: #202124;
        }
        .cv-search-filter-bar {
          display: flex;
          gap: 16px;
          margin-bottom: 32px;
          flex-wrap: wrap;
          align-items: center;
        }
        .cv-search-input {
          flex: 1;
          min-width: 320px;
          padding: 12px 48px 12px 16px;
          border: 1px solid #dadce0;
          border-radius: 24px;
          font-size: 14px;
          background: #ffffff;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 1px 2px rgba(60, 64, 67, 0.1);
          position: relative;
        }
        .cv-search-input::placeholder {
          color: #9aa0a6;
        }
        .cv-search-input:hover {
          box-shadow: 0 2px 5px rgba(60, 64, 67, 0.15);
        }
        .cv-search-input:focus {
          outline: none;
          border-color: #4285f4;
          box-shadow: 0 2px 8px rgba(66, 133, 244, 0.2);
        }
        .cv-filter-select {
          padding: 10px 16px;
          border: 1px solid #dadce0;
          border-radius: 4px;
          font-size: 14px;
          background: #ffffff;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          color: #202124;
          min-width: 140px;
        }
        .cv-filter-select:hover {
          background: #f8f9fa;
        }
        .cv-filter-select:focus {
          outline: none;
          border-color: #4285f4;
          box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.2);
        }
        .cv-favorites-toggle {
          padding: 10px 20px;
          border: 1px solid #dadce0;
          border-radius: 4px;
          background: ${showFavoritesOnly ? '#fef8e3' : '#ffffff'};
          color: ${showFavoritesOnly ? '#f9ab00' : '#5f6368'};
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .cv-favorites-toggle:hover {
          background: ${showFavoritesOnly ? '#fef8e3' : '#f8f9fa'};
          border-color: ${showFavoritesOnly ? '#f9ab00' : '#dadce0'};
        }
        .cv-favorites-toggle.active {
          background: #fef8e3;
          border-color: #f9ab00;
          color: #f9ab00;
        }
        .cv-templates-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          margin-bottom: 32px;
          padding-bottom: 24px;
          border-bottom: 1px solid #e8eaed;
        }
        .cv-templates-actions button {
          background: #1a73e8;
          color: #ffffff;
          border: none;
          border-radius: 4px;
          padding: 10px 24px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          align-items: center;
          gap: 8px;
          box-shadow: 0 1px 2px rgba(60, 64, 67, 0.3);
        }
        .cv-templates-actions button:hover:not(:disabled) {
          background: #1765cc;
          box-shadow: 0 2px 4px rgba(60, 64, 67, 0.3);
        }
        .cv-templates-actions button.secondary {
          background: #ffffff;
          color: #5f6368;
          border: 1px solid #dadce0;
          box-shadow: none;
        }
        .cv-templates-actions button.secondary:hover {
          background: #f8f9fa;
          box-shadow: 0 1px 2px rgba(60, 64, 67, 0.1);
        }
        .cv-templates-actions button.danger {
          background: #ea4335;
        }
        .cv-templates-actions button.danger:hover:not(:disabled) {
          background: #d33b2c;
        }
        .cv-templates-actions button:disabled {
          opacity: 0.38;
          cursor: not-allowed;
          background: #f1f3f4;
          color: #5f6368;
        }
        .cv-status-banner {
          padding: 14px 18px;
          border-radius: 10px;
          font-size: 14px;
          margin-bottom: 24px;
        }
        .cv-status-banner.info {
          background: rgba(59, 130, 246, 0.12);
          color: #1d4ed8;
          border-left: 4px solid #3b82f6;
        }
        .cv-status-banner.success {
          background: rgba(34, 197, 94, 0.12);
          color: #15803d;
          border-left: 4px solid #22c55e;
        }
        .cv-status-banner.warning {
          background: rgba(234, 179, 8, 0.12);
          color: #b45309;
          border-left: 4px solid #eab308;
        }
        .cv-toast-notification {
          position: fixed;
          top: 20px;
          right: 20px;
          background: #ffffff;
          border-radius: 4px;
          box-shadow: 0 2px 6px rgba(60, 64, 67, 0.3), 0 4px 8px 2px rgba(60, 64, 67, 0.15);
          min-width: 320px;
          max-width: 400px;
          z-index: 10000;
          overflow: hidden;
          animation: toastSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1px solid #dadce0;
        }
        @keyframes toastSlideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .cv-toast-content {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          position: relative;
        }
        .cv-toast-icon {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #22c55e;
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          animation: iconBounce 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }
        @keyframes iconBounce {
          0% {
            transform: scale(0);
          }
          50% {
            transform: scale(1.2);
          }
          100% {
            transform: scale(1);
          }
        }
        .cv-toast-message {
          flex: 1;
          font-size: 14px;
          font-weight: 400;
          color: #202124;
          line-height: 1.5;
        }
        .cv-toast-close {
          background: none;
          border: none;
          color: #5f6368;
          font-size: 20px;
          cursor: pointer;
          padding: 0;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          flex-shrink: 0;
        }
        .cv-toast-close:hover {
          background: #f1f3f4;
          color: #202124;
        }
        .cv-toast-progress-bar {
          position: absolute;
          bottom: 0;
          left: 0;
          height: 4px;
          background: #22c55e;
          animation: toastProgress 3s linear forwards;
          border-radius: 0 0 12px 12px;
        }
        @keyframes toastProgress {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
        .cv-templates-empty {
          border: 1px dashed #dadce0;
          border-radius: 8px;
          padding: 80px 32px;
          text-align: center;
          color: #5f6368;
          background: #ffffff;
        }
        .cv-templates-empty p {
          margin: 8px 0;
          font-size: 14px;
          color: #5f6368;
        }
        .cv-templates-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 24px;
        }
        @media (max-width: 1024px) {
          .cv-templates-grid {
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          }
        }
        @media (max-width: 640px) {
          .cv-templates-grid {
            grid-template-columns: 1fr;
          }
          .cv-templates-container {
            padding: 24px 16px;
          }
        }
        .cv-template-card {
          border: 1px solid #dadce0;
          border-radius: 8px;
          background: #ffffff;
          overflow: hidden;
          cursor: pointer;
          position: relative;
          box-shadow: 0 1px 2px rgba(60, 64, 67, 0.3), 0 1px 3px 1px rgba(60, 64, 67, 0.15);
          opacity: 0;
          animation: cardEntrance 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
          transform-origin: center;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @keyframes cardEntrance {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .cv-template-card:nth-child(1) { animation-delay: 0.05s; }
        .cv-template-card:nth-child(2) { animation-delay: 0.1s; }
        .cv-template-card:nth-child(3) { animation-delay: 0.15s; }
        .cv-template-card:nth-child(4) { animation-delay: 0.2s; }
        .cv-template-card:nth-child(5) { animation-delay: 0.25s; }
        .cv-template-card:nth-child(6) { animation-delay: 0.3s; }
        .cv-template-card:nth-child(7) { animation-delay: 0.35s; }
        .cv-template-card:nth-child(8) { animation-delay: 0.4s; }
        .cv-template-card:nth-child(9) { animation-delay: 0.45s; }
        .cv-template-card:nth-child(10) { animation-delay: 0.5s; }
        .cv-template-card:nth-child(11) { animation-delay: 0.55s; }
        .cv-template-card:nth-child(12) { animation-delay: 0.6s; }
        .cv-template-card:hover {
          box-shadow: 0 2px 6px rgba(60, 64, 67, 0.3), 0 4px 8px 2px rgba(60, 64, 67, 0.15);
          transform: translateY(-2px);
        }
        .cv-template-card:active {
          transform: translateY(0);
          box-shadow: 0 1px 2px rgba(60, 64, 67, 0.3), 0 1px 3px 1px rgba(60, 64, 67, 0.15);
        }
        .cv-template-card.favorite {
          border-color: #f9ab00;
        }
        .cv-thumbnail-favorite-badge {
          position: absolute;
          top: 12px;
          right: 12px;
          background: rgba(255, 255, 255, 0.95);
          border-radius: 50%;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 20;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          backdrop-filter: blur(8px);
          animation: favoritePulse 2s ease-in-out infinite;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes favoritePulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 4px 12px rgba(251, 191, 36, 0.3);
          }
        }
        .cv-template-card:hover .cv-thumbnail-favorite-badge {
          transform: scale(1.1);
          box-shadow: 0 2px 8px rgba(251, 191, 36, 0.2);
        }
        .cv-template-thumbnail-wrapper {
          width: 100%;
          height: 180px;
          position: relative;
          overflow: hidden;
        }
        .cv-template-thumbnail {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .cv-template-card:hover .cv-template-thumbnail {
          transform: scale(1.05);
        }
        .cv-thumbnail-content {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 24px;
          animation: contentFadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.2s both;
        }
        @keyframes contentFadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .cv-thumbnail-initials-circle {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: 700;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
          
          position: relative;
          overflow: hidden;
        }
        .cv-thumbnail-initials-circle::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.3);
          transform: translate(-50%, -50%);
          transition: width 0.6s ease, height 0.6s ease;
        }
        .cv-template-card:hover .cv-thumbnail-initials-circle {
          transform: scale(1.1) rotate(5deg);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        .cv-template-card:hover .cv-thumbnail-initials-circle::before {
          width: 100%;
          height: 100%;
        }
        .cv-thumbnail-name {
          font-size: 15px;
          font-weight: 600;
          color: #1e293b;
          text-align: center;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .cv-template-card:hover .cv-thumbnail-name {
          transform: translateY(-2px);
          color: #0f172a;
        }
        .cv-template-card-content {
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          background: #ffffff;
        }
        .cv-template-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
        }
        .cv-template-card-title {
          margin: 0;
          font-size: 16px;
          font-weight: 500;
          color: #202124;
          flex: 1;
          line-height: 1.4;
          letter-spacing: 0.1px;
        }
        .cv-template-card-title-input {
          width: 100%;
          padding: 8px 12px;
          border: 2px solid #3b82f6 !important;
          border-radius: 6px;
          font-size: 16px;
          font-weight: 600;
          font-family: inherit;
          background: #ffffff !important;
          color: #1e293b !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          animation: inputFocus 0.3s ease;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          z-index: 1000;
          position: relative;
        }
        @keyframes inputFocus {
          from {
            transform: scale(0.98);
            opacity: 0.8;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .cv-template-card-title-input:focus {
          outline: none !important;
          border-color: #2563eb !important;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15) !important;
        }
        .cv-template-badge {
          background: #f1f3f4;
          color: #5f6368;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          white-space: nowrap;
        }
        .cv-template-meta {
          display: flex;
          flex-direction: column;
          gap: 6px;
          font-size: 13px;
          color: #5f6368;
        }
        .cv-template-meta-item {
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .cv-template-card:hover .cv-template-meta-item {
          transform: translateX(2px);
        }
        .cv-template-meta-icon {
          font-size: 14px;
        }
        .cv-template-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 8px;
        }
        .cv-template-tag {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          background: #e8f0fe;
          color: #1a73e8;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          border: 1px solid #1a73e8;
        }
        .cv-template-tag-remove {
          background: none;
          border: none;
          color: #1a73e8;
          cursor: pointer;
          font-size: 16px;
          line-height: 1;
          padding: 0;
          width: 16px;
          height: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: all 0.2s ease;
        }
        .cv-template-tag-remove:hover {
          background: #1a73e8;
          color: #ffffff;
        }
        .cv-template-card-actions {
          display: flex;
          gap: 6px;
          opacity: 0;
          transform: translateY(5px);
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .cv-template-card:hover .cv-template-card-actions {
          opacity: 1;
          transform: translateY(0);
        }
        .cv-template-card-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 8px;
          gap: 8px;
        }
        .cv-template-card-action-btn {
          flex: 1;
          padding: 8px 16px;
          border: 1px solid #dadce0;
          border-radius: 4px;
          background: #ffffff;
          color: #5f6368;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }
        .cv-template-card-action-btn:hover {
          background: #f8f9fa;
          border-color: #dadce0;
        }
        .cv-template-card-action-btn.primary {
          background: #1a73e8;
          color: #ffffff;
          border-color: #1a73e8;
        }
        .cv-template-card-action-btn.primary:hover {
          background: #1765cc;
          border-color: #1765cc;
          box-shadow: 0 1px 2px rgba(60, 64, 67, 0.3);
        }
        .cv-template-card-action-btn.danger {
          background: #ffffff;
          color: #dc2626;
          border-color: #fecaca;
        }
        .cv-template-card-action-btn.danger:hover {
          background: #fef2f2;
          border-color: #fca5a5;
        }
        .cv-quick-actions-menu {
          position: relative;
          z-index: 100;
        }
        .cv-quick-actions-trigger {
          background: transparent;
          border: none;
          border-radius: 6px;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #64748b;
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          padding: 0;
          position: relative;
        }
        .cv-quick-actions-trigger::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.05);
          transform: translate(-50%, -50%);
          transition: width 0.4s ease, height 0.4s ease;
        }
        .cv-quick-actions-trigger:hover::before {
          width: 40px;
          height: 40px;
        }
        .cv-quick-actions-trigger:hover {
          background: #f1f5f9;
          color: #0f172a;
          transform: rotate(90deg);
        }
        .cv-quick-actions-dropdown {
          position: absolute;
          bottom: 100%;
          right: 0;
          margin-bottom: 8px;
          background: #ffffff;
          border: 1px solid #dadce0;
          border-radius: 4px;
          box-shadow: 0 2px 6px rgba(60, 64, 67, 0.3), 0 4px 8px 2px rgba(60, 64, 67, 0.15);
          z-index: 10000;
          min-width: 200px;
          overflow: hidden;
          animation: dropdownSlide 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @keyframes dropdownSlide {
          0% {
            opacity: 0;
            transform: translateY(8px) scale(0.95);
          }
          50% {
            opacity: 0.8;
            transform: translateY(-2px) scale(1.02);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .cv-quick-actions-dropdown button {
          width: 100%;
          padding: 12px 16px;
          border: none;
          background: #ffffff;
          color: #202124;
          font-size: 14px;
          text-align: left;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          align-items: center;
          gap: 12px;
          position: relative;
        }
        .cv-quick-actions-dropdown button:hover {
          background: #f8f9fa;
        }
        .cv-quick-actions-dropdown button.danger {
          color: #ea4335;
        }
        .cv-quick-actions-dropdown button.danger:hover {
          background: #fce8e6;
        }
        .cv-quick-actions-divider {
          height: 1px;
          background: #e8eaed;
          margin: 4px 0;
        }
        .cv-tag-input-container {
          padding: 8px 16px;
        }
        .cv-tag-input {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #dadce0;
          border-radius: 4px;
          font-size: 14px;
          background: #ffffff;
          color: #202124;
        }
        .cv-tag-input:focus {
          outline: none;
          border-color: #4285f4;
          box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.2);
        }
        /* Preview Modal Styles */
        .cv-preview-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(32, 33, 36, 0.6);
          backdrop-filter: blur(2px);
          z-index: 10000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          box-sizing: border-box;
          animation: fadeIn 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .cv-preview-modal-content {
          width: 100%;
          max-width: 1200px;
          max-height: 95vh;
          background: #ffffff;
          border-radius: 8px;
          box-shadow: 0 8px 10px 1px rgba(0, 0, 0, 0.14), 0 3px 14px 2px rgba(0, 0, 0, 0.12);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .cv-preview-modal-header {
          padding: 20px 24px;
          border-bottom: 1px solid #e8eaed;
          background: #ffffff;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
        }
        .cv-preview-modal-title {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
        }
        .cv-preview-modal-title h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 400;
          color: #202124;
          letter-spacing: 0.1px;
        }
        .cv-preview-template-badge {
          background: #1a73e8;
          color: #ffffff;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
        .cv-preview-modal-controls {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }
        .cv-preview-zoom-controls {
          display: flex;
          align-items: center;
          gap: 4px;
          background: #f8f9fa;
          padding: 4px;
          border-radius: 4px;
          border: 1px solid #dadce0;
        }
        .cv-zoom-btn {
          background: #ffffff;
          border: none;
          border-radius: 4px;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          color: #5f6368;
        }
        .cv-zoom-btn:hover:not(:disabled) {
          background: #f1f3f4;
        }
        .cv-zoom-btn:disabled {
          opacity: 0.38;
          cursor: not-allowed;
        }
        .cv-zoom-select {
          padding: 6px 12px;
          border: none;
          border-radius: 4px;
          background: #ffffff;
          font-size: 13px;
          cursor: pointer;
          color: #202124;
        }
        .cv-preview-modal-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .cv-preview-btn {
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          align-items: center;
          gap: 6px;
          box-shadow: 0 1px 2px rgba(60, 64, 67, 0.3);
        }
        .cv-preview-btn-primary {
          background: #1a73e8;
          color: #ffffff;
        }
        .cv-preview-btn-primary:hover:not(:disabled) {
          background: #1765cc;
          box-shadow: 0 2px 4px rgba(60, 64, 67, 0.3);
        }
        .cv-preview-btn-secondary {
          background: #ffffff;
          color: #5f6368;
          border: 1px solid #dadce0;
          box-shadow: none;
        }
        .cv-preview-btn-secondary:hover {
          background: #f8f9fa;
          box-shadow: 0 1px 2px rgba(60, 64, 67, 0.1);
        }
        .cv-preview-btn-danger {
          background: #ea4335;
          color: #ffffff;
        }
        .cv-preview-btn-danger:hover {
          background: #d33b2c;
          box-shadow: 0 2px 4px rgba(60, 64, 67, 0.3);
        }
        .cv-preview-btn-close {
          background: transparent;
          color: #5f6368;
          width: 40px;
          height: 40px;
          padding: 0;
          border-radius: 50%;
          font-size: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: none;
        }
        .cv-preview-btn-close:hover {
          background: #f1f3f4;
        }
        .cv-preview-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .cv-preview-modal-body {
          flex: 1;
          overflow: hidden;
          background: #f8f9fa;
          display: flex;
          flex-direction: column;
        }
        .cv-preview-scroll-container {
          width: 100%;
          flex: 1;
          overflow-y: auto;
          overflow-x: auto;
          padding: 24px;
          box-sizing: border-box;
          -webkit-overflow-scrolling: touch;
        }
        .cv-preview-wrapper {
          width: 100%;
          max-width: 210mm;
          margin: 0 auto;
          background: #ffffff;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          border-radius: 4px;
          overflow: visible;
          min-height: 297mm;
        }
        .cv-preview-wrapper * {
          max-width: 100%;
          box-sizing: border-box;
          overflow-wrap: break-word;
          word-wrap: break-word;
        }
        .cv-preview-wrapper .resume-paper,
        .cv-preview-wrapper .modern-resume-paper,
        .cv-preview-wrapper .tech-resume-preview,
        .cv-preview-wrapper #section-to-print {
          page-break-after: auto;
          page-break-inside: avoid;
          break-inside: avoid;
        }
        .cv-preview-wrapper .experience-item,
        .cv-preview-wrapper .education-item,
        .cv-preview-wrapper .project-item {
          page-break-inside: avoid;
          break-inside: avoid;
        }
        @media (max-width: 768px) {
          .cv-preview-modal-content {
            max-height: 100vh;
            border-radius: 0;
          }
          .cv-preview-modal-header {
            padding: 16px 20px;
            flex-direction: column;
            align-items: stretch;
          }
          .cv-preview-modal-controls {
            width: 100%;
            flex-direction: column;
          }
          .cv-preview-zoom-controls {
            width: 100%;
            justify-content: center;
          }
          .cv-preview-modal-actions {
            width: 100%;
            flex-direction: column;
          }
          .cv-preview-btn {
            width: 100%;
            justify-content: center;
          }
          .cv-preview-scroll-container {
            padding: 20px;
          }
        }
      `}</style>

      <Navbar />
      
      {/* Top Feature Bar */}
      <div className="bg-primary text-primary-foreground py-2 border-b border-primary/20">
        <Container>
          <div className="flex items-center justify-center gap-4 flex-wrap text-sm font-medium">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              <span>Professional Templates</span>
            </div>
            <Separator orientation="vertical" className="h-4 bg-primary-foreground/30" />
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              <span>ATS-Optimized</span>
            </div>
            <Separator orientation="vertical" className="h-4 bg-primary-foreground/30" />
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              <span>AI Writing Assistant</span>
            </div>
            <Separator orientation="vertical" className="h-4 bg-primary-foreground/30" />
            <div className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              <span>1-Click Export</span>
            </div>
          </div>
        </Container>
      </div>

      {/* Back Button - Shadcn design, minimal padding */}
      <Container>
        <div className="relative py-2">
          <Button
            variant="ghost"
            onClick={() => {
              navigate("/create-resume?view=templates");
              setTimeout(() => {
                window.scrollTo(0, 0);
              }, 100);
            }}
            size="sm"
            className="h-8 px-3 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </div>
      </Container>

      <div className="cv-templates-container">
        <div className="cv-templates-header">
          <h1>Resume Portfolio</h1>
          <p>
            Manage your professional resume collection. Search, filter, and organize your saved templates with advanced analytics and tagging.
          </p>
        </div>

        {/* Analytics Dashboard */}
        <div className="cv-analytics-dashboard">
          <div className="cv-analytics-card">
            <div className="cv-analytics-icon" style={{ background: 'rgba(26, 115, 232, 0.1)', color: '#1a73e8' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
            </div>
            <div className="cv-analytics-content">
              <div className="cv-analytics-value">{analytics.totalCVs}</div>
              <div className="cv-analytics-label">Total CVs</div>
            </div>
          </div>
          <div className="cv-analytics-card">
            <div className="cv-analytics-icon" style={{ background: 'rgba(52, 168, 83, 0.1)', color: '#34a853' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <div className="cv-analytics-content">
              <div className="cv-analytics-value">{analytics.recent7Days}</div>
              <div className="cv-analytics-label">Last 7 Days</div>
            </div>
          </div>
          <div className="cv-analytics-card">
            <div className="cv-analytics-icon" style={{ background: 'rgba(251, 188, 4, 0.1)', color: '#fbbc04' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
              </svg>
            </div>
            <div className="cv-analytics-content">
              <div className="cv-analytics-value">{analytics.favoritesCount}</div>
              <div className="cv-analytics-label">Favorites</div>
            </div>
          </div>
          <div className="cv-analytics-card">
            <div className="cv-analytics-icon" style={{ background: 'rgba(234, 67, 53, 0.1)', color: '#ea4335' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <div className="cv-analytics-content">
              <div className="cv-analytics-value">{analytics.mostUsedTemplate}</div>
              <div className="cv-analytics-label">Most Used ({analytics.mostUsedCount})</div>
            </div>
          </div>
        </div>

        {/* Advanced Filters Toggle */}
        <div className="cv-advanced-filters-toggle">
          <button
            className={`cv-advanced-toggle-btn ${showAdvancedFilters ? 'active' : ''}`}
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
            </svg>
            Advanced Filters
            {showAdvancedFilters ? ' ▲' : ' ▼'}
          </button>
        </div>

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <div className="cv-advanced-filters-panel">
            <div className="cv-advanced-filter-group">
              <label className="cv-filter-label">Template Types (Multi-select)</label>
              <div className="cv-multi-select-container">
                {getUniqueTemplateTypes().map(type => (
                  <label key={type} className="cv-multi-select-option">
                    <input
                      type="checkbox"
                      checked={selectedTemplateTypes.includes(type)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTemplateTypes([...selectedTemplateTypes, type]);
                        } else {
                          setSelectedTemplateTypes(selectedTemplateTypes.filter(t => t !== type));
                        }
                      }}
                    />
                    <span>{type}</span>
                  </label>
                ))}
                {selectedTemplateTypes.length > 0 && (
                  <button
                    className="cv-clear-filter-btn"
                    onClick={() => setSelectedTemplateTypes([])}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div className="cv-advanced-filter-group">
              <label className="cv-filter-label">Date Range</label>
              <div className="cv-date-range-container">
                <input
                  type="date"
                  className="cv-date-input"
                  value={dateRange.start || ''}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  placeholder="Start Date"
                />
                <span className="cv-date-separator">to</span>
                <input
                  type="date"
                  className="cv-date-input"
                  value={dateRange.end || ''}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  placeholder="End Date"
                />
                {(dateRange.start || dateRange.end) && (
                  <button
                    className="cv-clear-filter-btn"
                    onClick={() => setDateRange({ start: null, end: null })}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {allTags.length > 0 && (
              <div className="cv-advanced-filter-group">
                <label className="cv-filter-label">Tags</label>
                <div className="cv-tags-filter-container">
                  <div className="cv-tags-list">
                    {allTags.map(tag => (
                      <label key={tag} className="cv-tag-filter-option">
                        <input
                          type="checkbox"
                          checked={selectedTags.includes(tag)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTags([...selectedTags, tag]);
                            } else {
                              setSelectedTags(selectedTags.filter(t => t !== tag));
                            }
                          }}
                        />
                        <span className="cv-tag-badge">{tag}</span>
                      </label>
                    ))}
                  </div>
                  {selectedTags.length > 0 && (
                    <button
                      className="cv-clear-filter-btn"
                      onClick={() => setSelectedTags([])}
                    >
                      Clear Tags
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="cv-search-filter-bar">
          <input
            type="text"
            className="cv-search-input"
            placeholder="Search by name, template type, or content"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <select
            className="cv-filter-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">All Templates</option>
            {getUniqueTemplateTypes().map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <select
            className="cv-filter-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="date-desc">Newest First</option>
            <option value="date-asc">Oldest First</option>
            <option value="name-asc">Name (A-Z)</option>
            <option value="name-desc">Name (Z-A)</option>
            <option value="favorites-first">Favorites First</option>
          </select>
          <button
            className={`cv-favorites-toggle ${showFavoritesOnly ? 'active' : ''}`}
            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
          >
            ⭐ {showFavoritesOnly ? 'Show All' : 'Favorites Only'}
          </button>
        </div>

        <div className="cv-templates-actions">
          <button onClick={handleBack}>
            ← Back to Templates
          </button>
          <button
            className="secondary"
            onClick={() => navigate("/create-resume")}
          >
            ➕ Create New Resume
          </button>
          <button
            className="danger"
            onClick={handleClearAll}
            disabled={templates.length === 0}
          >
            🗑️ Clear All
          </button>
        </div>

        {statusMessage && (
          <div className={`cv-status-banner ${statusVariant}`}>
            {statusMessage}
          </div>
        )}

        {filteredTemplates.length === 0 ? (
          <div className="cv-templates-empty">
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>📄</div>
            <p style={{ fontSize: "18px", fontWeight: 600, color: "#1e293b" }}>
              {templates.length === 0
                ? "You haven't saved any CV templates yet."
                : "No templates match your search criteria."}
            </p>
            <p>
              {templates.length === 0
                ? "Open a resume in the editor, click Save, and it will appear here for quick reuse."
                : "Try adjusting your search or filters."}
            </p>
          </div>
        ) : (
          <div className="cv-templates-grid">
            {filteredTemplates.map((item) => {
              const isFavorite = favorites.has(item.id);
              const isRenaming = renamingId === item.id;
              
              // Log rename state for debugging
              if (isRenaming) {
                console.log('[CvTemplatesPage] Rendering card in rename mode:', item.id, 'renameValue:', renameValue);
              }
              
              return (
                <div
                  key={item.id}
                  className={`cv-template-card ${isFavorite ? 'favorite' : ''}`}
                  onClick={() => !isRenaming && handleCardClick(item)}
                >
                  <div className="cv-template-thumbnail-wrapper">
                    <TemplateThumbnail
                      templateId={item.templateId || item.template_name}
                      resumeData={item.data || {}}
                      isFavorite={isFavorite}
                    />
                  </div>
                  <div className="cv-template-card-content">
                    <div className="cv-template-card-header">
                      {isRenaming ? (
                        <input
                          type="text"
                          className="cv-template-card-title-input"
                          value={renameValue}
                          onChange={(e) => {
                            console.log('[CvTemplatesPage] Rename input onChange:', e.target.value);
                            setRenameValue(e.target.value);
                          }}
                          onBlur={(e) => {
                            console.log('[CvTemplatesPage] Rename input onBlur');
                            // Only submit if clicking outside, not if clicking another element
                            setTimeout(() => {
                              console.log('[CvTemplatesPage] onBlur timeout - renamingId:', renamingId, 'item.id:', item.id);
                              if (renamingId === item.id) {
                                console.log('[CvTemplatesPage] Submitting rename for:', item.id);
                                handleRenameSubmit(item.id);
                              }
                            }, 200);
                          }}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            console.log('[CvTemplatesPage] Rename input keyDown:', e.key);
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              console.log('[CvTemplatesPage] Enter pressed - submitting rename');
                              handleRenameSubmit(item.id);
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              console.log('[CvTemplatesPage] Escape pressed - canceling rename');
                              handleRenameCancel();
                            }
                          }}
                          onFocus={(e) => {
                            e.stopPropagation();
                            console.log('[CvTemplatesPage] Rename input focused');
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('[CvTemplatesPage] Rename input clicked');
                          }}
                          autoFocus
                          style={{ outline: 'none' }}
                          ref={(input) => {
                            if (input) {
                              console.log('[CvTemplatesPage] Rename input ref set, focusing...');
                              setTimeout(() => input.focus(), 0);
                            }
                          }}
                        />
                      ) : (
                        <h3 className="cv-template-card-title">
                          {item.name || "Untitled Resume"}
                        </h3>
                      )}
                      <span className="cv-template-badge">
                        {item.templateLabel || item.templateId || "Template"}
                      </span>
                    </div>
                    <div className="cv-template-meta">
                      <div className="cv-template-meta-item">
                        <span className="cv-template-meta-icon">📅</span>
                        <span>{formatRelativeTime(item.savedAt)}</span>
                      </div>
                    </div>
                    {(item.tags && item.tags.length > 0) && (
                      <div className="cv-template-tags">
                        {item.tags.map(tag => (
                          <span key={tag} className="cv-template-tag">
                            {tag}
                            <button
                              className="cv-template-tag-remove"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveTag(item.id, tag);
                              }}
                              title="Remove tag"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="cv-template-card-footer">
                      <div className="cv-template-card-actions">
                        <button
                          className="cv-template-card-action-btn primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCardClick(item);
                          }}
                        >
                          👁️ Preview
                        </button>
                        <button
                          className="cv-template-card-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(item.id);
                          }}
                        >
                          📥 Download
                        </button>
                      </div>
                      <QuickActionsMenu
                        template={item}
                        isFavorite={isFavorite}
                        onEdit={() => {
                          console.log('[CvTemplatesPage] onEdit called for:', item.id);
                          handleOpen(item.id);
                        }}
                        onDownload={() => {
                          console.log('[CvTemplatesPage] onDownload called for:', item.id);
                          handleDownload(item.id);
                        }}
                        onRename={() => {
                          console.log('[CvTemplatesPage] onRename callback called for item:', item);
                          handleRename(item);
                        }}
                        onDelete={() => {
                          if (window.confirm("Are you sure you want to delete this template?")) {
                            handleDelete(item.id);
                          }
                        }}
                        onToggleFavorite={() => handleToggleFavorite(item)}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showPreview && selectedTemplate && (
        <PreviewModal
          template={selectedTemplate}
          zoom={previewZoom}
          onZoomChange={setPreviewZoom}
          onClose={() => {
            setShowPreview(false);
            setSelectedTemplate(null);
            setPreviewZoom(100);
          }}
          onDownload={() => handleDownload(selectedTemplate.id)}
          onEdit={() => {
            setShowPreview(false);
            handleOpen(selectedTemplate.id);
          }}
          onDelete={() => {
            if (window.confirm("Are you sure you want to delete this template?")) {
              handleDelete(selectedTemplate.id);
              setShowPreview(false);
              setSelectedTemplate(null);
            }
          }}
        />
      )}

      <Footer />

      {/* Toast Notification */}
      {showToast && (
        <div className="cv-toast-notification">
          <div className="cv-toast-content">
            <div className="cv-toast-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <span className="cv-toast-message">{toastMessage}</span>
            <button 
              className="cv-toast-close"
              onClick={() => {
                setShowToast(false);
                setTimeout(() => setToastMessage(""), 300);
              }}
            >
              ×
            </button>
          </div>
          <div className="cv-toast-progress-bar"></div>
        </div>
      )}
    </div>
  );
};

export default CvTemplatesPage;
